import React, { useEffect, useState } from 'react';
import { getAllPayments, approvePayment, rejectPayment, getPaymentDetail, getPaymentSummary } from '../../api/payments';
import { Icon } from '@iconify/react';
import toast from 'react-hot-toast';
import Pagination from '../../components/Pagination';
import { useLoader } from '../../context/LoaderContext';

const AllPayments = () => {
    const [payments, setPayments] = useState([]);
    const [summaryData, setSummaryData] = useState(null);
    const { showLoader, hideLoader } = useLoader();
    const [loading, setLoading] = useState(false);
    const [selectedPayment, setSelectedPayment] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [showDetailModal, setShowDetailModal] = useState(false);

    // Pagination States
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [displayPayments, setDisplayPayments] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [totalCount, setTotalCount] = useState(0);

    // Process Modal States
    const [showProcessModal, setShowProcessModal] = useState(false);
    const [processType, setProcessType] = useState(null);
    const [processData, setProcessData] = useState({ transactionId: '', comment: '', file: null });
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchPayments = async () => {
        showLoader();
        try {
            const [paymentsRes, summaryRes] = await Promise.all([
                getAllPayments(),
                getPaymentSummary().catch(err => {
                    console.error("Summary fetch error", err);
                    return null;
                })
            ]);

            if (Array.isArray(paymentsRes)) {
                setPayments(paymentsRes);
            } else if (paymentsRes.data && Array.isArray(paymentsRes.data)) {
                setPayments(paymentsRes.data);
            } else {
                setPayments([]);
            }

            if (summaryRes) {
                setSummaryData(summaryRes);
            }

        } catch (error) {
            console.error("Payments fetch error:", error);
            toast.error(error.message || "Bir hata oluştu.");
        } finally {
            hideLoader();
        }
    };

    useEffect(() => {
        fetchPayments();
    }, []);

    useEffect(() => {
        let filtered = payments;

        if (searchTerm) {
            filtered = filtered.filter(p =>
                p.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.providerTransactionId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                p.amount?.toString().includes(searchTerm)
            );
        }

        setTotalCount(filtered.length);

        const indexOfLastItem = currentPage * pageSize;
        const indexOfFirstItem = indexOfLastItem - pageSize;
        setDisplayPayments(filtered.slice(indexOfFirstItem, indexOfLastItem));
    }, [payments, currentPage, pageSize, searchTerm]);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    useEffect(() => {
        setCurrentPage(1);
    }, [pageSize, searchTerm]);

    const totalPages = Math.ceil(totalCount / pageSize);

    const openDetailModal = async (payment) => {
        const tId = payment.providerTransactionId;
        if (!tId) {
            toast.error("İşlem ID (Transaction ID) bulunamadı!");
            return;
        }

        setDetailLoading(true);
        try {
            const detailRes = await getPaymentDetail(tId);
            setSelectedPayment(detailRes);
            setShowDetailModal(true);
        } catch (error) {
            console.error("Detail fetch error:", error);
            toast.error(error.message || "Detaylar alınamadı.");
        } finally {
            setDetailLoading(false);
        }
    };

    const closeDetailModal = () => {
        setShowDetailModal(false);
        setSelectedPayment(null);
    };

    const openProcessModal = (payment, type) => {
        setProcessType(type);
        setProcessData({
            transactionId: payment.providerTransactionId || payment.id,
            comment: '',
            file: null
        });
        setShowProcessModal(true);
    };

    const closeProcessModal = () => {
        setShowProcessModal(false);
        setProcessType(null);
        setProcessData({ transactionId: '', comment: '', file: null });
    };

    const handleProcessChange = (e) => {
        if (e.target.name === 'file') {
            setProcessData({ ...processData, file: e.target.files[0] });
        } else {
            setProcessData({ ...processData, [e.target.name]: e.target.value });
        }
    };

    const submitProcess = async () => {
        if (!processData.transactionId) {
            toast.error("Transaction ID bulunamadı.");
            return;
        }

        setIsProcessing(true);
        const toastId = toast.loading("İşlem yapılıyor...");

        try {
            let res;
            if (processType === 'approve') {
                res = await approvePayment(processData);
            } else {
                res = await rejectPayment(processData);
            }
            toast.success(res.message || "İşlem başarılı!", { id: toastId });
            closeProcessModal();
            fetchPayments();
        } catch (error) {
            console.error("Process error:", error);
            toast.error(error.message || "İşlem başarısız.", { id: toastId });
        } finally {
            setIsProcessing(false);
        }
    };

    const renderDetailModalContent = () => {
        if (!selectedPayment) return null;

        return (
            <div className="row g-3">
                {Object.entries(selectedPayment).map(([key, value]) => {
                    if (key === 'userInfo' && typeof value === 'object' && value !== null) {
                        return (
                            <div className="col-12" key={key}>
                                <div className="card bg-neutral-50 border radius-8 p-12">
                                    <h6 className="text-sm fw-bold text-primary-light mb-2">Kullanıcı Bilgileri</h6>
                                    <div className="d-flex flex-wrap gap-3 text-sm">
                                        {Object.entries(value).map(([uKey, uValue]) => (
                                            <div key={uKey}>
                                                <span className="text-secondary-light fw-medium me-1">{uKey}:</span>
                                                <span className="fw-semibold">{uValue}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    }

                    if (key === 'adminFilePath' && value) {
                        return (
                            <div className="col-12" key={key}>
                                <label className="fw-semibold text-secondary-light text-xs text-uppercase mb-1">
                                    DOSYA
                                </label>
                                <div>
                                    <a href={value} target="_blank" rel="noopener noreferrer" className="btn btn-primary-50 text-primary-600 btn-sm d-inline-flex align-items-center gap-2 radius-8">
                                        <Icon icon="heroicons:document-text" />
                                        Dosyayı Görüntüle / İndir
                                    </a>
                                </div>
                            </div>
                        );
                    }

                    return (
                        <div className="col-md-6" key={key}>
                            <label className="fw-semibold text-secondary-light text-xs text-uppercase mb-1">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                            </label>
                            <div className="p-12 bg-neutral-50 radius-8 border text-wrap text-break text-sm">
                                {value === null || value === undefined || value === '' ? (
                                    <span className="text-muted fst-italic">-</span>
                                ) : (key.toLowerCase() !== 'status' && (key.toLowerCase().includes('date') || key.toLowerCase().endsWith('at'))) ? (
                                    new Date(value).toLocaleString('tr-TR')
                                ) : (typeof value === 'boolean') ? (
                                    value ? <span className="text-success fw-bold">Evet</span> : <span className="text-danger fw-bold">Hayır</span>
                                ) : (typeof value === 'object') ? (
                                    <pre className="mb-0 text-xs">{JSON.stringify(value, null, 2)}</pre>
                                ) : (
                                    String(value)
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        );
    };

    // Helper to format currency
    const formatCurrency = (amount) => {
        if (amount === undefined || amount === null) return '0';
        return amount.toLocaleString('tr-TR', { minimumFractionDigits: 1, maximumFractionDigits: 2 });
    };

    // Helper to process summary data into 3 groups
    const getProcessedSummary = () => {
        if (!summaryData) return [];

        // Parse values from specific keys
        const approvedCount = Number(summaryData.approvedCount) || 0;
        const approvedTotal = Number(summaryData.approvedTotal) || 0;
        const rejectedCount = Number(summaryData.rejectedCount) || 0;
        const rejectedTotal = Number(summaryData.rejectedTotal) || 0;
        const pendingCount = Number(summaryData.pendingCount) || 0;
        const pendingTotal = Number(summaryData.pendingTotal) || 0;

        // Group: Success (Approved)
        const successGroup = {
            title: 'BAŞARILI İŞLEMLER',
            count: approvedCount,
            amount: approvedTotal,
            icon: 'heroicons:check-circle',
            color: 'success'
        };

        // Group: Rejected
        const rejectedGroup = {
            title: 'REDDEDİLEN İŞLEMLER',
            count: rejectedCount,
            amount: rejectedTotal,
            icon: 'heroicons:x-circle',
            color: 'danger'
        };

        // Group: Pending
        const pendingGroup = {
            title: 'BEKLEYEN İŞLEMLER',
            count: pendingCount,
            amount: pendingTotal,
            icon: 'heroicons:clock',
            color: 'warning'
        };

        // Return order: Success, Rejected, Pending
        return [successGroup, rejectedGroup, pendingGroup];
    };

    const processedSummary = getProcessedSummary();


    return (
        <>
            {/* Summary Cards */}
            {summaryData && (
                <div className="row g-3 mb-24">
                    {processedSummary.map((item, index) => (
                        <div className="col-md-4" key={index}>
                            <div className="card p-24 radius-12 shadow-sm border-0 h-100 d-flex flex-row align-items-center gap-3">
                                <div className={`w-50-px h-50-px radius-12 d-flex align-items-center justify-content-center bg-${item.color}-50 text-${item.color}-600`}>
                                    <Icon icon={item.icon} className="text-2xl" />
                                </div>
                                <div>
                                    <p className="text-secondary-light text-sm mb-1 text-uppercase fw-semibold">
                                        {item.title}
                                    </p>
                                    <h6 className="mb-0 fw-bold text-xl">
                                        {item.count} / {formatCurrency(item.amount)}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div className="card h-100 p-0 radius-12">
                <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center justify-content-between flex-wrap gap-3">
                    <div className="d-flex align-items-center gap-3">
                        <h6 className="text-lg fw-semibold mb-0">Tüm Ödemeler</h6>
                        <span className="badge bg-primary-600 text-white px-12 py-6 radius-4">
                            Toplam: {totalCount}
                        </span>
                    </div>
                    <div className="navbar-search w-auto">
                        <div className="input-group radius-8 border">
                            <span className="input-group-text bg-base border-0 text-secondary-light">
                                <Icon icon="ion:search-outline" />
                            </span>
                            <input
                                type="text"
                                className="form-control bg-base border-0"
                                placeholder="Ara (Ad, Email, İşlem ID)..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                </div>
                <div className="card-body p-24">
                    <div className="table-responsive">
                        <table className="table bordered-table mb-0">
                            <thead>
                                <tr>
                                    <th scope="col">Kullanıcı</th>
                                    <th scope="col">Tutar</th>
                                    <th scope="col">Durum</th>
                                    <th scope="col">Yöntem</th>
                                    <th scope="col">Tarih</th>
                                    <th scope="col" className="text-center">İşlem</th>
                                </tr>
                            </thead>
                            <tbody>
                                {displayPayments.length > 0 ? (
                                    displayPayments.map((payment) => (
                                        <tr key={payment.id}>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="text-md fw-medium text-secondary-light">{payment.userName}</span>
                                                    <span className="text-xs text-secondary-light opacity-75">{payment.userEmail}</span>
                                                </div>
                                            </td>
                                            <td>
                                                <span className="text-primary-600 fw-bold">
                                                    {payment.amount} {payment.currency}
                                                </span>
                                            </td>
                                            <td>
                                                <span className={`badge px-12 py-6 radius-4 ${payment.status === 'Success' ? 'bg-success-focus text-success-main' :
                                                    payment.status === 'Failed' ? 'bg-danger-focus text-danger-main' :
                                                        'bg-warning-focus text-warning-main'
                                                    }`}>
                                                    {payment.status}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="text-secondary-light">{payment.paymentMethod}</span>
                                            </td>
                                            <td>
                                                <span className="text-secondary-light text-sm">
                                                    {new Date(payment.createdAt).toLocaleString('tr-TR')}
                                                </span>
                                            </td>
                                            <td className="text-center">
                                                <div className="d-flex align-items-center justify-content-center gap-2">
                                                    <button
                                                        className="w-32-px h-32-px bg-primary-50 text-primary-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-primary-600 hover-text-white transition-2"
                                                        onClick={() => openDetailModal(payment)}
                                                        title="Detay"
                                                    >
                                                        <Icon icon="heroicons:eye" className="text-xl" />
                                                    </button>
                                                    {payment.status !== 'Success' && payment.status !== 'Failed' && (
                                                        <>
                                                            <button
                                                                className="w-32-px h-32-px bg-success-50 text-success-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-success-600 hover-text-white transition-2"
                                                                onClick={() => openProcessModal(payment, 'approve')}
                                                                title="Onayla"
                                                            >
                                                                <Icon icon="heroicons:check" className="text-xl" />
                                                            </button>
                                                            <button
                                                                className="w-32-px h-32-px bg-danger-50 text-danger-600 rounded-circle d-inline-flex align-items-center justify-content-center hover-bg-danger-600 hover-text-white transition-2"
                                                                onClick={() => openProcessModal(payment, 'reject')}
                                                                title="Reddet"
                                                            >
                                                                <Icon icon="heroicons:x-mark" className="text-xl" />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="text-center py-24">
                                            <p className="text-secondary-light mb-0">Kayıtlı ödeme bulunamadı.</p>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>

                    <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        pageSize={pageSize}
                        setPageSize={setPageSize}
                        totalCount={totalCount}
                    />
                </div>
            </div>

            {/* Detail Modal */}
            {showDetailModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered modal-lg" role="document">
                        <div className="modal-content radius-16">
                            <div className="modal-header">
                                <h5 className="modal-title">Ödeme Detayı</h5>
                                <button type="button" className="btn-close" onClick={closeDetailModal}></button>
                            </div>
                            <div className="modal-body">
                                {detailLoading ? (
                                    <div className="text-center py-24">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                    </div>
                                ) : (
                                    renderDetailModalContent()
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeDetailModal}>Kapat</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Process Modal (Approve/Reject) */}
            {showProcessModal && (
                <div className="modal fade show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: "rgba(0,0,0,0.5)" }}>
                    <div className="modal-dialog modal-dialog-centered" role="document">
                        <div className="modal-content radius-16">
                            <div className={`modal-header ${processType === 'approve' ? 'bg-success-focus text-success-main' : 'bg-danger-focus text-danger-main'}`}>
                                <h5 className="modal-title d-flex align-items-center gap-2">
                                    <Icon icon={processType === 'approve' ? 'heroicons:check-circle' : 'heroicons:x-circle'} />
                                    {processType === 'approve' ? 'Ödemeyi Onayla' : 'Ödemeyi Reddet'}
                                </h5>
                                <button type="button" className="btn-close" onClick={closeProcessModal}></button>
                            </div>
                            <div className="modal-body">
                                <p className="mb-3">
                                    Transaction ID: <strong>{processData.transactionId}</strong>
                                </p>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Açıklama (Opsiyonel)</label>
                                    <textarea
                                        className="form-control"
                                        name="comment"
                                        rows="3"
                                        value={processData.comment}
                                        onChange={handleProcessChange}
                                        placeholder="İşlem hakkında not ekleyin..."
                                    ></textarea>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-semibold">Dosya (Opsiyonel)</label>
                                    <input
                                        type="file"
                                        className="form-control"
                                        name="file"
                                        onChange={handleProcessChange}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeProcessModal}>İptal</button>
                                <button
                                    type="button"
                                    className={`btn ${processType === 'approve' ? 'btn-success-600' : 'btn-danger-600'}`}
                                    onClick={submitProcess}
                                    disabled={isProcessing}
                                >
                                    {isProcessing ? 'İşleniyor...' : (processType === 'approve' ? 'Onayla' : 'Reddet')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default AllPayments;
